import React from 'react'

import {
  Badge,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Grid,
  GridItem,
  Table,
  TableContainer,
  Text,
  Thead,
  Tr,
  Tbody,
  Th,
  Td,
  DrawerCloseButton,
  Button,
  HStack,
} from '@chakra-ui/react'
import { gray, green } from 'src/components/__legacy__/color/palette'
import { Spacer } from 'src/components/__legacy__/spacer/spacer'

import {
  IcfDocumentCheckRole,
  IcfDocumentDetail as IcfDocumentDetailType,
  IcfDocumentRevision,
  IcfDocumentSetForIcfDocumentDetail,
  IcfDocumentSignerRole,
  icfDocumentSignFieldTypeText,
  icfDocumentSignFieldTypes,
  icfDocumentSignFieldTypeToRole,
  icfDocumentTypes,
  icfDocumentTypeText,
} from '../entity'

type Props = {
  document: IcfDocumentDetailType | undefined
  revision: IcfDocumentRevision | undefined
  filePreviewUrl: string
  onRedirectDocuSignPreview: ({
    dsTemplateId,
    role,
  }: {
    dsTemplateId: string
    role: IcfDocumentSignerRole | IcfDocumentCheckRole
  }) => void
}

type TableKey = keyof Pick<
  IcfDocumentSetForIcfDocumentDetail,
  'name' | 'enabled'
>

const tableCellWidth: Record<TableKey, string | undefined> = {
  name: undefined,
  enabled: '120px',
}

export const IcfDocumentDetailRenewal: React.FC<Props> = ({
  document,
  revision,
  filePreviewUrl,
  onRedirectDocuSignPreview,
}) => {
  if (!document || !revision) return null

  return (
    <>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <Spacer size={16} />
        <DrawerBody>
          <Grid templateColumns="max-content 1fr" gap="6" alignItems="center">
            <GridItem>
              <Text fontWeight="medium" fontSize="sm" color="gray.600">
                文書ID
              </Text>
            </GridItem>
            <GridItem>
              <Text>{document.numberingId}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="medium" fontSize="sm" color="gray.600">
                文書名
              </Text>
            </GridItem>
            <GridItem>
              <Text>{revision.name}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="medium" fontSize="sm" color="gray.600">
                文書の種類
              </Text>
            </GridItem>
            <GridItem>
              <Text>{icfDocumentTypeText[revision.type]}</Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="medium" fontSize="sm" color="gray.600">
                患者の同意
              </Text>
            </GridItem>
            <GridItem>
              {/* TODO: isRequiredPatientSignがfalseの場合の表示を確認 */}
              <Text>
                {revision.isRequiredPatientSign ? '必須' : '必須ではない'}
              </Text>
            </GridItem>
            <GridItem>
              <Text fontWeight="medium" fontSize="sm" color="gray.600">
                ファイル名
              </Text>
            </GridItem>
            <GridItem>
              <Button
                as="a"
                href={filePreviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="link"
                minW="auto"
              >
                {revision.fileName}
              </Button>
            </GridItem>
            {revision.type === icfDocumentTypes.AgreementForm && (
              <>
                <GridItem>
                  <Text fontWeight="medium" fontSize="sm" color="gray.600">
                    署名ロール
                  </Text>
                </GridItem>
                <GridItem>
                  <HStack>
                    {icfDocumentSignFieldTypes.map(sfType => (
                      <Button
                        minW="auto"
                        key={icfDocumentSignFieldTypeToRole[sfType]}
                        variant="link"
                        alignItems="left"
                        isDisabled={
                          !revision.signFields.some(
                            f => f.signFieldType === sfType,
                          )
                        }
                        onClick={() =>
                          onRedirectDocuSignPreview({
                            dsTemplateId: revision.dsTemplateId,
                            role: icfDocumentSignFieldTypeToRole[sfType],
                          })
                        }
                      >
                        {icfDocumentSignFieldTypeText[sfType]}
                      </Button>
                    ))}
                  </HStack>
                </GridItem>
              </>
            )}
          </Grid>

          <Text as="h3" mt="10" fontWeight="bold">
            文書セット
          </Text>
          <Spacer size={8} />
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th width={tableCellWidth['enabled']}>ステータス</Th>
                  <Th width={tableCellWidth['name']}>文書セット</Th>
                </Tr>
              </Thead>
              <Tbody>
                {document.sets.map(set => (
                  <Tr key={set.uid}>
                    <Td width={tableCellWidth['enabled']}>
                      <Badge
                        sx={{
                          width: '80px',
                          height: '24px',
                          display: 'flex',
                          justifyContent: 'center',
                          color: set.enabled ? green[40] : gray[55],
                          backgroundColor: set.enabled ? green[10] : gray[5],
                          fontWeight: 'normal',
                        }}
                      >
                        {set.enabled ? '有効' : '無効'}
                      </Badge>
                    </Td>
                    <Td width={tableCellWidth['name']}>
                      {set.enabled ? (
                        <Text>
                          {set.numberingId}. {set.name}
                        </Text>
                      ) : (
                        <Text color="gray.200">
                          {set.numberingId}. {set.name}
                        </Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </DrawerBody>
      </DrawerContent>
    </>
  )
}
